import { Add, Remove } from "@mui/icons-material";
import { Box, Button, Popover, Typography, useTheme } from "@mui/material";
import { ControlledTextFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-text-field";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { FC, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { ExtendedFieldError } from "vmd-types";
/**
 * An entry for semaphore status with three threshold colors.
 */
export interface CategoryEntry {
  name: string;
  color?: string;
  label: string;
  placeholder: string;
  order?: number;
}

export interface CategoryFieldsProps {
  /**
   * Initial status category entries.
   */
  initialEntries?: CategoryEntry[];
  /**
   * Called when entries (add/remove/update) change.
   */
  onEntriesChange?: (entries: CategoryEntry[]) => void;

  fieldName?: string;

  onFieldNameChange?: (name: string) => void;

  validationErrors: Record<string, ExtendedFieldError>;

  baseFieldName: string;

  language?: string;
}

export const CategoryFields: FC<CategoryFieldsProps> = ({
  initialEntries = [],
  onEntriesChange,
  fieldName = "Severity",
  onFieldNameChange = () => {},
  validationErrors,
  baseFieldName,
  language = "en",
}) => {
  const theme = useTheme();

  const [entries, setEntries] = useState<CategoryEntry[]>(initialEntries);
  const [maximumAllowedEntries] = useState(5);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const notifyChange = (newEntries: CategoryEntry[]) => {
    setEntries(newEntries);
    onEntriesChange?.(newEntries);
  };

  const handleNameChange = (index: number, name: string) => {
    const updated = [...entries];
    updated[index].name = name;
    notifyChange(updated);
  };

  const addEntry = () => {
    if (entries.length < maximumAllowedEntries) {
      notifyChange(
        [
          ...entries,
          {
            name: "",
            label:
              getLocalizedMessage(language, "categoryField.newEntry") ||
              "New Entry",
            placeholder:
              getLocalizedMessage(language, "categoryField.enterLowStatus") ||
              "Enter low status",
          },
        ].map((entry, index) => ({ ...entry, order: index }))
      );
    }
  };

  const handleColorChange = (index: number, color: string) => {
    const updated = [...entries];
    updated[index].color = color;
    notifyChange(updated);
  };

  const togglePicker = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    if (openIndex === index) {
      setOpenIndex(null);
      setAnchorEl(null);
    } else {
      setOpenIndex(index);
      setAnchorEl(event.currentTarget);
    }
  };

  const removeEntry = (index: number) => {
    notifyChange(entries.filter((_, i) => i !== index));
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 10,
        width: "100%",
        border: "1px solid #E0E0E0",
        padding: 2,
        borderRadius: "12px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontWeight: 500, width: 280, fontSize: 14 }}>
            {getLocalizedMessage(language, "categoryField.nameOfCategory") ||
              "Name of the Category"}
          </Typography>
          <ControlledTextFormField
            value={fieldName}
            onChange={onFieldNameChange}
            error={validationErrors?.[`${baseFieldName}`] !== undefined}
            helperText={validationErrors?.[`${baseFieldName}`]?.message}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 15 }}>
          <Typography sx={{ fontWeight: 500, width: 280, fontSize: 14 }}>
            {getLocalizedMessage(language, "categoryField.categoryLabel") ||
              "Category Label"}
            *
          </Typography>
        </Box>

        {entries.map((entry, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "row",

              gap: 5,
              alignItems: "center",
            }}
          >
            <Box sx={{ width: 360 }}>
              <ControlledTextFormField
                value={entry.name}
                onChange={(val) => handleNameChange(index, val)}
                error={
                  validationErrors?.[`${baseFieldName}.${index}.name`] !==
                  undefined
                }
                helperText={
                  validationErrors?.[`${baseFieldName}.${index}.name`]?.message
                }
              />
            </Box>

            {entry.color && (
              <Box sx={{ display: "flex", gap: 4 }}>
                <Box>
                  <Box
                    onClick={(e) => togglePicker(e, index)}
                    sx={{
                      height: 30,
                      width: 30,
                      background: entry.color,
                      "&:hover": {
                        background: `${entry.color}50`,
                      },
                      borderRadius: "50%",
                    }}
                  />
                  <Popover
                    open={openIndex === index}
                    anchorEl={anchorEl}
                    onClose={() => setOpenIndex(null)}
                    sx={{ zIndex: 2000, borderRadius: "12px" }}
                  >
                    <HexColorPicker
                      color={entry.color}
                      onChange={(color) => handleColorChange(index, color)}
                    />
                  </Popover>
                </Box>
              </Box>
            )}
            <Button
              onClick={() => removeEntry(index)}
              sx={{ color: theme.palette.primary.dark, fontSize: 30, ml: 5 }}
            >
              <Remove />
            </Button>
          </Box>
        ))}

        {entries.length < maximumAllowedEntries && (
          <Button
            onClick={addEntry}
            sx={{ color: theme.palette.primary.dark, fontSize: 30 }}
          >
            <Add />
          </Button>
        )}
      </Box>
    </Box>
  );
};
